import { useEffect, useState } from "react";
import { History, Loader2 } from "lucide-react";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";

interface Row {
  id: string;
  kind: string;
  date_value: string;
  title: string;
}

/** /timeline - past-facing chronology. Year selector, then
 *  month-by-month. Respects active view scope. */
export function Timeline() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(String(thisYear));
  const [byMonth, setByMonth] = useState<Record<string, Row[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/timeline?year=${year}`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { byMonth: {} }))
      .then((j) => setByMonth(j.byMonth ?? {}))
      .catch(() => setByMonth({}))
      .finally(() => setLoading(false));
  }, [year]);

  const months = Object.keys(byMonth).sort().reverse();
  const years = Array.from({ length: 6 }, (_, i) => String(thisYear - i));

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb segments={[{ label: "Timeline" }]} />
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            Timeline
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            What already happened.
          </p>
        </div>
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </header>

      <TeachingBanner id="timeline_overview" />

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : months.length === 0 ? (
        <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-6 text-sm text-slate-500 dark:text-slate-400">
          Nothing recorded for {year} in this view.
        </div>
      ) : (
        months.map((m) => (
          <section key={m}>
            <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">
              {m}
            </h2>
            <ul className="space-y-2">
              {byMonth[m].map((r, i) => (
                <li
                  key={`${r.id}-${i}`}
                  className="glass rounded-lg border border-slate-200/70 dark:border-slate-700/50 p-3 flex items-start gap-3"
                >
                  <History className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {r.title}
                    </div>
                    <div className="text-[11px] text-slate-400 dark:text-slate-500">
                      {r.date_value} · {r.kind}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
